
import React from 'react';
import { Booth } from '../types';
import { X, ExternalLink, Hash, Users, BookOpen, Heart } from 'lucide-react';

interface InfoDrawerProps {
  booth: Booth | null;
  isFavorite: boolean;
  onToggleFavorite: (boothId: string) => void;
  onClose: () => void;
}

const InfoDrawer: React.FC<InfoDrawerProps> = ({ booth, isFavorite, onToggleFavorite, onClose }) => {
  if (!booth) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Semi-transparent backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" 
        onClick={onClose}
      />
      
      <div 
        className="relative bg-white w-[90%] max-w-[90%] md:max-w-2xl rounded-3xl overflow-hidden shadow-2xl animate-pop-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Banner Area */}
        <div className="relative w-full aspect-[768/472] bg-gray-100">
           <img 
            src={booth.bannerUrl} 
            alt="Banner" 
            className="w-full h-full object-cover"
          />
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-black/40 text-white rounded-full hover:bg-black/60 transition-colors z-20"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content Area */}
        <div className="p-6 sm:p-8 space-y-6">
          <div className="flex items-end gap-4 -mt-12 sm:-mt-16 relative z-10">
            <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full border-4 border-white shadow-lg bg-white overflow-hidden shrink-0">
              <img src={booth.avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 pb-1 min-w-0">
              <h2 className="text-xl sm:text-3xl font-black text-gray-800 leading-tight flex items-center gap-3">
                <span className="hidden sm:inline-block text-orange-600 font-black px-2.5 py-1 bg-orange-50 rounded-xl text-xl shrink-0">#{booth.id}</span>
                <span className="truncate">{booth.circleName}</span>
              </h2>
              <div className="flex items-center gap-2 text-gray-500 text-xs sm:text-base font-medium mt-1">
                <span className="sm:hidden text-orange-600 font-bold px-1.5 py-0.5 bg-orange-50 rounded-md text-xs shrink-0">#{booth.id}</span>
                <span className="truncate">{booth.author}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex flex-wrap gap-2">
              {booth.tags.map(tag => (
                <span key={tag} className="px-2 py-0.5 sm:px-3 sm:py-1 bg-orange-100 text-orange-700 text-[10px] sm:text-sm font-black rounded-lg uppercase">
                  {tag}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {booth.keywords.map(kw => (
                <span key={kw} className="text-xs sm:text-sm font-bold text-blue-500 hover:underline cursor-pointer">#{kw}</span>
              ))}
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto pr-3 scrollbar-thin">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen size={14} className="text-gray-400 sm:w-4 sm:h-4" />
              <h3 className="text-xs sm:text-sm font-black text-gray-400 uppercase tracking-wider">宣傳文</h3>
            </div>
            {/* 支援 HTML 渲染的區塊 */}
            <div 
              className="rich-text-content text-xs sm:text-base text-gray-700 leading-relaxed font-medium"
              dangerouslySetInnerHTML={{ __html: booth.description }}
            />
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => onToggleFavorite(booth.id)}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black transition-all text-xs sm:text-base active:scale-95 shadow-lg border-2 ${
                  isFavorite 
                    ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600' 
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Heart size={18} className={`sm:w-5 sm:h-5 ${isFavorite ? 'fill-current' : ''}`} />
                {isFavorite ? '已收藏' : '加入最愛'}
              </button>
              
              <a 
                href={booth.socialUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 sm:gap-3 py-3 sm:py-4 bg-gray-900 text-white font-black rounded-xl sm:rounded-2xl hover:bg-black transition-all text-xs sm:text-base active:scale-95 shadow-lg"
              >
                <ExternalLink size={18} className="sm:w-5 sm:h-5" />
                工商連結
              </a>
            </div>
        </div>
      </div>
      
      <style>{`
        @keyframes pop-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-pop-in {
          animation: pop-in 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        .scrollbar-thin::-webkit-scrollbar {
          width: 5px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background-color: #e5e7eb;
          border-radius: 10px;
        }
        /* 針對宣傳文 HTML 的基本樣式 */
        .rich-text-content a { color: #3b82f6; text-decoration: underline; }
        .rich-text-content strong { font-weight: 800; }
        .rich-text-content p { margin-bottom: 0.5em; }
        .rich-text-content ul { list-style-type: disc; margin-left: 1.5rem; margin-bottom: 1rem; }
        .rich-text-content ol { list-style-type: decimal; margin-left: 1.5rem; margin-bottom: 1rem; }
      `}</style>
    </div>
  );
};

export default InfoDrawer;
